<script setup lang="ts">
import type { CTItem } from '@/types/vegco/ctitem'
import type { MDBVegcoBucket } from '@/types/vegco/bucket'
import { notifyError } from '@/utils/helpers'
import { getFournisseurLettresDappel } from '@/utils/helpers-vegco'



const CODE_PRODUIT = 'LRC7X6VC-P'
const CULTURE = 'Laitue Romaine'

declare interface VgcoRequete {
    id: string
    demandéPar: string
    dateCréé: string
    type: string
    paletteId: string
}

const theme = useTheme()
const { smAndDown } = useDisplay()
const isLoading = ref(false)
const buckets = ref<MDBVegcoBucket[]>([])
const requetes = ref<VgcoRequete[]>([])

const pendingIds = computed(() => new Set(requetes.value.map(r => r.paletteId)))

declare interface RecolteGroup {
    dateRécolte: string
    items: CTItem[]
    totalQuantité: number
}

declare interface SemiGroup {
    semiId: string
    récoltes: RecolteGroup[]
    totalQuantité: number
    quarantaineCount: number
}

declare interface FournisseurGroup {
    fournisseurId: string
    semis: SemiGroup[]
    totalQuantité: number
    quarantaineCount: number
}

const allItems = computed(() => buckets.value.flatMap(b => b.items))

const grouped = computed((): FournisseurGroup[] => {
    const byFournisseur = new Map<string, Map<string, Map<string, CTItem[]>>>()

    for (const item of allItems.value) {
        const dateKey =
            item.dateRécolte && new Date(item.dateRécolte).getFullYear() > 2000
                ? new Date(item.dateRécolte).toLocaleDateString('fr-CA')
                : '—'

        if (!byFournisseur.has(item.fournisseurId))
            byFournisseur.set(item.fournisseurId, new Map())

        const bySemi = byFournisseur.get(item.fournisseurId)!

        if (!bySemi.has(item.semiId))
            bySemi.set(item.semiId, new Map())

        const byDate = bySemi.get(item.semiId)!

        if (!byDate.has(dateKey))
            byDate.set(dateKey, [])

        byDate.get(dateKey)!.push(item)
    }

    const result: FournisseurGroup[] = []
    for (const [fournisseurId, bySemi] of byFournisseur) {
        const semis: SemiGroup[] = []
        let fournisseurTotal = 0
        let fournisseurQuarantaine = 0

        for (const [semiId, byDate] of bySemi) {
            const récoltes: RecolteGroup[] = []
            let semiTotal = 0
            let semiQuarantaine = 0

            for (const [dateRécolte, items] of byDate) {
                const qtéRécolte = items.reduce((s, i) => s + i.quantité, 0)
                récoltes.push({ dateRécolte, items, totalQuantité: qtéRécolte })
                semiTotal += qtéRécolte
                semiQuarantaine += items.filter(i => i.état === 'Q').length
            }

            semis.push({ semiId, récoltes, totalQuantité: semiTotal, quarantaineCount: semiQuarantaine })
            fournisseurTotal += semiTotal
            fournisseurQuarantaine += semiQuarantaine
        }

        result.push({ fournisseurId, semis, totalQuantité: fournisseurTotal, quarantaineCount: fournisseurQuarantaine })
    }

    return result
})

const totalItems = computed(() => allItems.value.length)
const quarantaineCount = computed(() => allItems.value.filter(i => i.état === 'Q').length)

async function getData() {
    if (isLoading.value) return
    try {
        isLoading.value = true
        const [inventaireRes, requetesRes] = await Promise.all([
            apiProphet.get('vgco/buckets/inventaire', { params: { culture: CULTURE, codeProduit: CODE_PRODUIT } }),
            apiProphet.get('vgco/requetes'),
        ])
        buckets.value = inventaireRes.data as MDBVegcoBucket[]
        requetes.value = requetesRes.data as VgcoRequete[]
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

async function refreshRequetes() {
    try {
        const res = await apiProphet.get('vgco/requetes')
        requetes.value = res.data as VgcoRequete[]
    } catch {
        // silently ignore poll errors
    }
}

function formatÉtat(état: string) {
    if (état === 'Q') return 'Quarantaine'
    if (état === 'V') return 'Valide'
    return état
}

function étatColor(état: string) {
    return état === 'Q' ? 'warning' : 'success'
}

function étatIcon(état: string) {
    return état === 'Q' ? 'M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z' : 'M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z'
}

function formatRéception(date: string) {
    return new Date(date).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' })
}

onMounted(getData)

let pollInterval: ReturnType<typeof setInterval> | null = null
onMounted(() => { pollInterval = setInterval(refreshRequetes, 10_000) })
onUnmounted(() => { if (pollInterval) clearInterval(pollInterval) })

function palettesQ(items: CTItem[]) {
    return items.filter(i => i.état === 'Q').map(i => i.paletteId)
}

function palettesV(items: CTItem[]) {
    return items.filter(i => i.état !== 'Q').map(i => i.paletteId)
}

function applyÉtatLocal(paletteIds: string[], nouvelÉtat: string) {
    const idSet = new Set(paletteIds)
    for (const bucket of buckets.value)
        for (const item of bucket.items)
            if (idSet.has(item.paletteId))
                item.état = nouvelÉtat
}

const pendingOriginalÉtat = new Map<string, string>()

function addLocalPending(paletteIds: string[], type: string) {
    const fakes: VgcoRequete[] = paletteIds.map(id => ({
        id: `local-${id}`,
        demandéPar: '',
        dateCréé: new Date().toISOString(),
        type,
        paletteId: id,
    }))
    requetes.value = [
        ...requetes.value.filter(r => !paletteIds.includes(r.paletteId)),
        ...fakes,
    ]
}

function removeLocalPending(paletteIds: string[]) {
    const idSet = new Set(paletteIds)
    requetes.value = requetes.value.filter(
        r => !(r.id.startsWith('local-') && idSet.has(r.paletteId))
    )
}

async function relacherPalettes(paletteIds: string[]) {
    if (!paletteIds.length) return
    for (const id of paletteIds) {
        const item = allItems.value.find(i => i.paletteId === id)
        if (item) pendingOriginalÉtat.set(id, item.état)
    }
    applyÉtatLocal(paletteIds, 'V')
    addLocalPending(paletteIds, 'relache')
    try {
        await apiProphet.post('vgco/requetes/relachequarantaine', paletteIds)
        removeLocalPending(paletteIds)
        await refreshRequetes()
    } catch (e) {
        removeLocalPending(paletteIds)
        for (const id of paletteIds) {
            const orig = pendingOriginalÉtat.get(id)
            if (orig !== undefined) applyÉtatLocal([id], orig)
            pendingOriginalÉtat.delete(id)
        }
        notifyError(e)
    }
}

async function mettrEnQuarantaine(paletteIds: string[]) {
    if (!paletteIds.length) return
    for (const id of paletteIds) {
        const item = allItems.value.find(i => i.paletteId === id)
        if (item) pendingOriginalÉtat.set(id, item.état)
    }
    applyÉtatLocal(paletteIds, 'Q')
    addLocalPending(paletteIds, 'quarantaine')
    try {
        await apiProphet.post('vgco/requetes/remettrequarantaine', paletteIds)
        removeLocalPending(paletteIds)
        await refreshRequetes()
    } catch (e) {
        removeLocalPending(paletteIds)
        for (const id of paletteIds) {
            const orig = pendingOriginalÉtat.get(id)
            if (orig !== undefined) applyÉtatLocal([id], orig)
            pendingOriginalÉtat.delete(id)
        }
        notifyError(e)
    }
}

const confirmDialog = ref(false)
const confirmRequeteId = ref<string | null>(null)
const confirmPaletteId = ref<string | null>(null)

function promptAnnulerRequete(paletteId: string) {
    const requete = requetes.value.find(r => r.paletteId === paletteId)
    if (!requete) return
    confirmRequeteId.value = requete.id
    confirmPaletteId.value = paletteId
    confirmDialog.value = true
}

async function confirmerAnnulation() {
    if (!confirmRequeteId.value || !confirmPaletteId.value) return
    const id = confirmRequeteId.value
    const paletteId = confirmPaletteId.value
    confirmDialog.value = false
    confirmRequeteId.value = null
    confirmPaletteId.value = null
    requetes.value = requetes.value.filter(r => r.id !== id)
    const originalÉtat = pendingOriginalÉtat.get(paletteId)
    if (originalÉtat !== undefined) {
        applyÉtatLocal([paletteId], originalÉtat)
        pendingOriginalÉtat.delete(paletteId)
    }
    if (id.startsWith('local-')) return
    try {
        await apiProphet.delete(`vgco/requetes/${id}`)
    } catch (e) {
        await refreshRequetes()
        notifyError(e)
    }
}
</script>

<template>
    <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading"
        style="height: 85vh; display: flex; flex-direction: column;">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

                <!-- Left: icon + title/subtitle -->
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="warning" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L10.11,5.22L16,8.61L17.96,7.5L12,4.15M6.04,7.5L12,10.85L13.96,9.75L8.08,6.35L6.04,7.5M5,15.91L11,19.29V12.58L5,9.21V15.91M19,15.91V9.21L13,12.58V19.29L19,15.91Z" size="26" />
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Quarantaines</div>
                        <div class="text-caption text-medium-emphasis">{{ CODE_PRODUIT }} · {{ CULTURE }}</div>
                    </div>
                    <v-divider vertical class="mx-2" style="height: 32px; align-self: center;" />
                    <v-chip v-if="!isLoading && totalItems > 0" color="primary" variant="tonal" size="small"
                        prepend-icon="M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z">
                        {{ totalItems }} palettes
                    </v-chip>
                    <v-chip v-if="!isLoading && quarantaineCount > 0" color="warning" variant="tonal" size="small"
                        :prepend-icon="smAndDown ? undefined : 'M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z'">
                        {{ quarantaineCount }}{{ smAndDown ? ' Q' : ' en quarantaine' }}
                    </v-chip>
                </div>

                <!-- Right: refresh button -->
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn color="primary" variant="tonal" rounded="pill" prepend-icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                        :loading="isLoading" @click="getData">
                        Actualiser
                    </v-btn>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Scrollable content -->
        <div style="flex: 1 1 auto; overflow-x: auto; overflow-y: auto;">

            <!-- Loading skeleton -->
            <div v-if="isLoading" class="pa-6 d-flex justify-center">
                <v-progress-circular indeterminate color="primary" />
            </div>

            <!-- Empty state -->
            <div v-else-if="grouped.length === 0"
                class="d-flex flex-column align-center justify-center text-medium-emphasis"
                style="height: 200px; gap: 8px;">
                <v-icon icon="M2,10.96C1.5,10.68 1.35,10.07 1.63,9.59L3.13,7C3.24,6.8 3.41,6.66 3.6,6.58L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.66,6.72 20.82,6.88 20.91,7.08L22.36,9.6C22.64,10.08 22.47,10.69 22,10.96L21,11.54V16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V10.96C2.7,11.13 2.32,11.14 2,10.96M12,4.15V4.15L12,10.85V10.85L17.96,7.5L12,4.15M5,15.91L11,19.29V12.58L5,9.21V15.91M19,15.91V12.69L14,15.59C13.67,15.77 13.3,15.76 13,15.6V19.29L19,15.91M13.85,13.36L20.13,9.73L19.55,8.72L13.27,12.35L13.85,13.36Z" size="40" />
                <span class="text-body-2">Aucun item en inventaire</span>
            </div>

            <!-- Grouped content -->
            <v-expansion-panels v-else variant="accordion" class="ma-4" style="gap: 8px;">
                <v-expansion-panel
                    v-for="fournisseur in grouped"
                    :key="fournisseur.fournisseurId"
                    rounded="lg"
                    elevation="1">

                    <!-- Fournisseur header -->
                    <v-expansion-panel-title>
                        <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                            <v-avatar color="primary" variant="tonal" size="32" rounded="md">
                                <v-icon icon="M12,5A3.5,3.5 0 0,0 8.5,8.5A3.5,3.5 0 0,0 12,12A3.5,3.5 0 0,0 15.5,8.5A3.5,3.5 0 0,0 12,5M12,7A1.5,1.5 0 0,1 13.5,8.5A1.5,1.5 0 0,1 12,10A1.5,1.5 0 0,1 10.5,8.5A1.5,1.5 0 0,1 12,7M5.5,8A2.5,2.5 0 0,0 3,10.5C3,11.44 3.53,12.25 4.29,12.68C4.65,12.88 5.06,13 5.5,13C5.94,13 6.35,12.88 6.71,12.68C7.08,12.47 7.39,12.17 7.62,11.81C6.89,10.86 6.5,9.7 6.5,8.5C6.5,8.41 6.5,8.31 6.5,8.22C6.2,8.08 5.86,8 5.5,8M18.5,8C18.14,8 17.8,8.08 17.5,8.22C17.5,8.31 17.5,8.41 17.5,8.5C17.5,9.7 17.11,10.86 16.38,11.81C16.5,12 16.63,12.15 16.78,12.3C16.94,12.45 17.1,12.58 17.29,12.68C17.65,12.88 18.06,13 18.5,13C18.94,13 19.35,12.88 19.71,12.68C20.47,12.25 21,11.44 21,10.5A2.5,2.5 0 0,0 18.5,8M12,14C9.66,14 5,15.17 5,17.5V19H19V17.5C19,15.17 14.34,14 12,14M4.71,14.55C2.78,14.78 0,15.76 0,17.5V19H3V17.07C3,16.06 3.69,15.22 4.71,14.55M19.29,14.55C20.31,15.22 21,16.06 21,17.07V19H24V17.5C24,15.76 21.22,14.78 19.29,14.55M12,16C13.53,16 15.24,16.5 16.23,17H7.77C8.76,16.5 10.47,16 12,16Z" size="18" />
                            </v-avatar>
                            <span class="text-subtitle-2 font-weight-bold">
                                {{ getFournisseurLettresDappel(fournisseur.fournisseurId) }}
                            </span>
                            <v-chip size="x-small" color="primary" variant="tonal">
                                {{ fournisseur.semis.length }} semi<span v-if="fournisseur.semis.length > 1">s</span>
                            </v-chip>
                            <v-chip size="x-small" color="secondary" variant="tonal"
                                prepend-icon="M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z">
                                {{ fournisseur.totalQuantité }} unités
                            </v-chip>
                            <v-chip v-if="fournisseur.quarantaineCount > 0" size="x-small" color="warning"
                                variant="tonal" prepend-icon="M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z">
                                {{ fournisseur.quarantaineCount }} Q
                            </v-chip>
                        </div>
                    </v-expansion-panel-title>

                    <v-expansion-panel-text class="pa-0">
                        <!-- Semi groups -->
                        <div v-for="semi in fournisseur.semis" :key="semi.semiId" class="semi-group">

                            <!-- Semi header -->
                            <div class="semi-header px-4 py-2" style="gap: 6px;">
                                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                                    <v-icon icon="M23,4.1V2.3L21.2,2.1C21.1,2.1 20.5,2 19.5,2C15.4,2 12.4,3.2 10.7,5.3C9.4,4.5 7.6,4 5.5,4C4.5,4 3.8,4.1 3.8,4.1L1.9,4.4L2,6.1C2.1,9.1 3.6,14.8 8.8,14.8C8.9,14.8 8.9,14.8 9,14.8V18.2C5.2,18.7 2,20 2,20V22H22V20C22,20 18.8,18.7 15,18.2V15C21.3,14.9 23,7.8 23,4.1M12,18C11.7,18 11.3,18 11,18V12.4C11,12.4 10.8,9 8,9C8,9 9.5,9.8 9.9,12.7C9.5,12.8 9.1,12.8 8.8,12.8C4.2,12.8 4,6.1 4,6.1C4,6.1 4.6,6 5.5,6C7.4,6 10.5,6.4 11.4,9.1C11.9,4.6 17,4 19.5,4C20.4,4 21,4.1 21,4.1C21,4.1 21,13.1 14.7,13.1C14.5,13.1 14.2,13.1 14,13.1C14,11.1 16,8.1 16,8.1C13,9.1 13,13 13,13V18C12.7,18 12.3,18 12,18Z" size="16" color="success" />
                                    <span class="text-body-2 font-weight-bold">Semi {{ semi.semiId }}</span>
                                    <v-chip size="x-small" color="success" variant="tonal"
                                        prepend-icon="M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z">
                                        {{ semi.totalQuantité }} unités
                                    </v-chip>
                                    <v-chip v-if="semi.quarantaineCount > 0" size="x-small" color="warning"
                                        variant="tonal" prepend-icon="M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z">
                                        {{ semi.quarantaineCount }} Q
                                    </v-chip>
                                    <v-spacer />
                                    <template v-if="!smAndDown">
                                        <v-btn v-if="palettesQ(semi.récoltes.flatMap(r => r.items)).length === semi.récoltes.flatMap(r => r.items).length"
                                            size="x-small" color="success" variant="tonal"
                                            rounded="pill" prepend-icon="M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10A2,2 0 0,1 6,8H15V6A3,3 0 0,0 12,3A3,3 0 0,0 9,6H7A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,17A2,2 0 0,1 10,15A2,2 0 0,1 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17Z"
                                            @click="relacherPalettes(palettesQ(semi.récoltes.flatMap(r => r.items)))">
                                            Relâcher tout le semi
                                        </v-btn>
                                        <v-btn v-else
                                            size="x-small" color="warning" variant="tonal"
                                            rounded="pill" prepend-icon="M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z"
                                            @click="mettrEnQuarantaine(palettesV(semi.récoltes.flatMap(r => r.items)))">
                                            Mettre en quarantaine
                                        </v-btn>
                                    </template>
                                </div>
                                <div v-if="smAndDown" class="mt-2">
                                    <v-btn v-if="palettesQ(semi.récoltes.flatMap(r => r.items)).length === semi.récoltes.flatMap(r => r.items).length"
                                        size="small" color="success" variant="tonal" rounded="pill"
                                        prepend-icon="M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10A2,2 0 0,1 6,8H15V6A3,3 0 0,0 12,3A3,3 0 0,0 9,6H7A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,17A2,2 0 0,1 10,15A2,2 0 0,1 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17Z" block
                                        @click="relacherPalettes(palettesQ(semi.récoltes.flatMap(r => r.items)))">
                                        Relâcher tout le semi
                                    </v-btn>
                                    <v-btn v-else
                                        size="small" color="warning" variant="tonal" rounded="pill"
                                        prepend-icon="M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z" block
                                        @click="mettrEnQuarantaine(palettesV(semi.récoltes.flatMap(r => r.items)))">
                                        Mettre en quarantaine
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Récolte date groups -->
                            <div v-for="récolte in semi.récoltes" :key="récolte.dateRécolte">

                                <!-- Date subheader -->
                                <div class="date-header px-4 py-2" style="gap: 6px;">
                                    <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                                        <v-icon icon="M12 12H17V17H12V12M19 3H18V1H16V3H8V1H6V3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3M19 5V7H5V5H19M5 19V9H19V19H5Z" size="14"
                                            class="text-medium-emphasis" />
                                        <span class="text-caption text-medium-emphasis">
                                            Récolte : {{ récolte.dateRécolte }}
                                        </span>
                                        <span class="text-caption text-medium-emphasis">
                                            · {{ récolte.items.length }} palette<span
                                                v-if="récolte.items.length > 1">s</span>
                                            · {{ récolte.totalQuantité }} unités
                                        </span>
                                        <v-spacer />
                                        <template v-if="!smAndDown">
                                            <v-btn v-if="palettesQ(récolte.items).length === récolte.items.length"
                                                size="x-small" color="success" variant="tonal"
                                                rounded="pill" prepend-icon="M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10A2,2 0 0,1 6,8H15V6A3,3 0 0,0 12,3A3,3 0 0,0 9,6H7A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,17A2,2 0 0,1 10,15A2,2 0 0,1 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17Z"
                                                @click="relacherPalettes(palettesQ(récolte.items))">
                                                Relâcher la récolte
                                            </v-btn>
                                            <v-btn v-else
                                                size="x-small" color="warning" variant="tonal"
                                                rounded="pill" prepend-icon="M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z"
                                                @click="mettrEnQuarantaine(palettesV(récolte.items))">
                                                Mettre en quarantaine
                                            </v-btn>
                                        </template>
                                    </div>
                                    <div v-if="smAndDown" class="mt-2">
                                        <v-btn v-if="palettesQ(récolte.items).length === récolte.items.length"
                                            size="small" color="success" variant="tonal" rounded="pill"
                                            prepend-icon="M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10A2,2 0 0,1 6,8H15V6A3,3 0 0,0 12,3A3,3 0 0,0 9,6H7A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,17A2,2 0 0,1 10,15A2,2 0 0,1 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17Z" block
                                            @click="relacherPalettes(palettesQ(récolte.items))">
                                            Relâcher la récolte
                                        </v-btn>
                                        <v-btn v-else
                                            size="small" color="warning" variant="tonal" rounded="pill"
                                            prepend-icon="M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z" block
                                            @click="mettrEnQuarantaine(palettesV(récolte.items))">
                                            Mettre en quarantaine
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- Items: table on desktop, cards on mobile -->
                                <v-table v-if="!smAndDown" density="compact" class="mx-4 mb-2 items-table"
                                    rounded="lg">
                                    <thead>
                                        <tr>
                                            <th class="text-left">Palette ID</th>
                                            <th class="text-left">État</th>
                                            <th class="text-left">Réception</th>
                                            <th class="text-right">Quantité</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in récolte.items" :key="item.paletteId">
                                            <td class="text-body-2 font-weight-medium">
                                                {{ item.paletteId }}
                                            </td>
                                            <td>
                                                <v-chip v-if="pendingIds.has(item.paletteId)" color="info" variant="tonal"
                                                    size="x-small" style="cursor:pointer"
                                                    @click="promptAnnulerRequete(item.paletteId)">
                                                    <template #prepend>
                                                        <v-progress-circular indeterminate size="10" width="2" class="mr-1" />
                                                    </template>
                                                    en cours
                                                </v-chip>
                                                <v-chip v-else :color="étatColor(item.état)" variant="tonal"
                                                    size="x-small" :prepend-icon="étatIcon(item.état)">
                                                    {{ formatÉtat(item.état) }}
                                                </v-chip>
                                            </td>
                                            <td class="text-body-2 text-medium-emphasis">
                                                {{ formatRéception(item.dateRéception) }}
                                            </td>
                                            <td class="text-right text-body-2">{{ item.quantité }}</td>
                                            <td class="text-right">
                                                <v-btn v-if="item.état === 'Q'" size="x-small" color="success" variant="text"
                                                    rounded="pill" prepend-icon="M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10A2,2 0 0,1 6,8H15V6A3,3 0 0,0 12,3A3,3 0 0,0 9,6H7A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,17A2,2 0 0,1 10,15A2,2 0 0,1 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17Z"
                                                    :disabled="pendingIds.has(item.paletteId)"
                                                    @click="relacherPalettes([item.paletteId])">
                                                    Relâcher
                                                </v-btn>
                                                <v-btn v-else size="x-small" color="warning" variant="text"
                                                    rounded="pill" prepend-icon="M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z"
                                                    :disabled="pendingIds.has(item.paletteId)"
                                                    @click="mettrEnQuarantaine([item.paletteId])">
                                                    Mettre en quarantaine
                                                </v-btn>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-table>

                                <!-- Mobile cards -->
                                <div v-else class="px-3 pb-3" style="display: flex; flex-direction: column; gap: 8px;">
                                    <v-card v-for="item in récolte.items" :key="item.paletteId"
                                        variant="outlined" rounded="lg" class="palette-card pa-3">
                                        <div class="d-flex align-center justify-space-between mb-1">
                                            <span class="text-body-2 font-weight-bold">{{ item.paletteId }}</span>
                                            <v-chip v-if="pendingIds.has(item.paletteId)" color="info" variant="tonal"
                                                size="x-small" style="cursor:pointer"
                                                @click="promptAnnulerRequete(item.paletteId)">
                                                <template #prepend>
                                                    <v-progress-circular indeterminate size="10" width="2" class="mr-1" />
                                                </template>
                                                en cours
                                            </v-chip>
                                            <v-chip v-else :color="étatColor(item.état)" variant="tonal"
                                                size="x-small" :prepend-icon="étatIcon(item.état)">
                                                {{ formatÉtat(item.état) }}
                                            </v-chip>
                                        </div>
                                        <div class="d-flex align-center justify-space-between">
                                            <div style="display: flex; flex-direction: column; gap: 2px;">
                                                <span class="text-caption text-medium-emphasis">
                                                    {{ formatRéception(item.dateRéception) }}
                                                </span>
                                                <span class="text-caption">
                                                    Quantité : <strong>{{ item.quantité }}</strong>
                                                </span>
                                            </div>
                                            <v-btn v-if="item.état === 'Q'" size="x-small" color="success" variant="tonal"
                                                rounded="pill" prepend-icon="M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10A2,2 0 0,1 6,8H15V6A3,3 0 0,0 12,3A3,3 0 0,0 9,6H7A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,17A2,2 0 0,1 10,15A2,2 0 0,1 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17Z"
                                                :disabled="pendingIds.has(item.paletteId)"
                                                @click="relacherPalettes([item.paletteId])">
                                                Relâcher
                                            </v-btn>
                                            <v-btn v-else size="x-small" color="warning" variant="tonal"
                                                rounded="pill" prepend-icon="M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z"
                                                :disabled="pendingIds.has(item.paletteId)"
                                                @click="mettrEnQuarantaine([item.paletteId])">
                                                Mettre en quarantaine
                                            </v-btn>
                                        </div>
                                    </v-card>
                                </div>

                            </div>
                        </div>
                    </v-expansion-panel-text>

                </v-expansion-panel>
            </v-expansion-panels>

        </div>

    </v-card>

    <!-- Confirm annulation dialog -->
    <v-dialog v-model="confirmDialog" max-width="400" persistent>
        <v-card rounded="xl">
            <v-card-title class="text-subtitle-1 font-weight-bold pt-5 px-5">
                Annuler la requête ?
            </v-card-title>
            <v-card-text class="px-5 text-body-2 text-medium-emphasis">
                La requête en attente pour la palette
                <strong>{{ confirmPaletteId }}</strong> sera supprimée.
                Cette action est irréversible.
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
                <v-spacer />
                <v-btn variant="text" @click="confirmDialog = false">Annuler</v-btn>
                <v-btn color="error" variant="tonal" rounded="pill" @click="confirmerAnnulation">
                    Supprimer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Semi group ───────────────────────────── */
.semi-group {
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.semi-header {
    background: rgba(var(--v-theme-surface-variant), 0.12);
}

/* ── Date subheader ───────────────────────── */
.date-header {
    background: rgba(var(--v-theme-surface-variant), 0.06);
}

/* ── Items table ──────────────────────────── */
.items-table {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
